// src/utils/horizontalCharts.js
// Функции для рендеринга горизонтальных диаграмм (студенты, предметы)

import {
  createFormatters,
  easeOutC,
  clamp,
  niceStep,
  buildPalette,
  positionTooltip,
  createSVGElement,
  createSVGGroup,
  createSVGLine,
  createSVGText,
} from "./chartUtils";

const { fmtNum } = createFormatters();

// Диаграмма средней оценки студентов по курсу (с пагинацией по 20 человек)
export function makeStudentsAverageChart(
  cardEl,
  courseId,
  courseName,
  studentsData,
  chartRegistry,
  root
) {
  chartRegistry.set(cardEl.id, {
    type: "studentsAvg",
    data: {
      courseId,
      courseName,
      students: JSON.parse(JSON.stringify(studentsData)),
    },
  });

  const chart = cardEl.querySelector(".chart");
  const svg = chart.querySelector("svg");
  const tooltip = chart.querySelector(".tooltip");
  let legend = cardEl.querySelector(".legend");
  if (!legend) {
    legend = document.createElement("div");
    legend.className = "legend";
    cardEl.appendChild(legend);
  }

  const WIDTH = 960;
  const M = { top: 18, right: 60, bottom: 44, left: 280 };
  const innerW = WIDTH - M.left - M.right;

  // ── пустое состояние ──
  if (!studentsData || studentsData.length === 0) {
    svg.setAttribute("viewBox", `0 0 ${WIDTH} 200`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    legend.innerHTML = "";
    const emptyItem = document.createElement("div");
    emptyItem.className = "legend-item";
    emptyItem.textContent = "Ma'lumot yo'q";
    legend.appendChild(emptyItem);
    return;
  }

  const sortedStudents = [...studentsData].sort(
    (a, b) => (b.avgMark || 0) - (a.avgMark || 0)
  );

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedStudents.length / PAGE_SIZE);
  let currentPage = 0;

  // Палитра строится один раз для всех студентов — цвет стабилен при смене страниц
  const { colors, hovers } = buildPalette(sortedStudents.length);

  // ── блок пагинации ──
  let paginationEl = cardEl.querySelector(".s-pagination");
  if (!paginationEl) {
    paginationEl = document.createElement("div");
    paginationEl.className = "s-pagination";
    chart.parentNode.insertBefore(paginationEl, chart);
  }

  // ── рендер одной страницы SVG ──
  function renderPage(pageIdx) {
    currentPage = pageIdx;
    const slice = sortedStudents.slice(pageIdx * PAGE_SIZE, (pageIdx + 1) * PAGE_SIZE);

    // Пагинация-контролы
    if (totalPages > 1) {
      paginationEl.style.cssText =
        "display:flex;align-items:center;gap:10px;padding:4px 0 10px;";
      paginationEl.innerHTML = "";

      const mkBtn = (label, disabled) => {
        const btn = document.createElement("button");
        btn.textContent = label;
        btn.disabled = disabled;
        btn.style.cssText = [
          "padding:4px 14px;border-radius:6px;font-size:14px;font-weight:600;",
          "border:1px solid #e5e8f0;background:#fff;transition:background .15s;",
          disabled ? "color:#c4c9d4;cursor:default;" : "color:#3a6cf6;cursor:pointer;",
        ].join("");
        return btn;
      };

      const prevBtn = mkBtn("←", pageIdx === 0);
      prevBtn.addEventListener("click", () => renderPage(currentPage - 1));

      const info = document.createElement("span");
      info.style.cssText = "flex:1;text-align:center;font-size:13px;color:#5d6676;";
      const startN = pageIdx * PAGE_SIZE + 1;
      const endN = Math.min((pageIdx + 1) * PAGE_SIZE, sortedStudents.length);
      info.textContent = `${startN}–${endN} / ${sortedStudents.length} ta talaba  •  ${pageIdx + 1}/${totalPages} bet`;

      const nextBtn = mkBtn("→", pageIdx === totalPages - 1);
      nextBtn.addEventListener("click", () => renderPage(currentPage + 1));

      paginationEl.appendChild(prevBtn);
      paginationEl.appendChild(info);
      paginationEl.appendChild(nextBtn);
    } else {
      paginationEl.style.cssText = "display:none;";
    }

    // Динамическая высота SVG под кол-во студентов на странице
    const BAR_SLOT = 26;
    const HEIGHT = Math.max(200, slice.length * BAR_SLOT + M.top + M.bottom);
    const innerH = HEIGHT - M.top - M.bottom;

    svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const g   = createSVGGroup(svg, `translate(${M.left},${M.top})`, null, root);
    const gridG  = createSVGGroup(g, null, null, root);
    const barsG  = createSVGGroup(g, null, null, root);
    const axisXG = createSVGGroup(g, null, null, root);
    const axisYG = createSVGGroup(g, null, null, root);

    const xStep = niceStep(100, 6);
    const xMax  = Math.ceil(100 / xStep) * xStep || 1;
    const xScale = (val) => Math.round((val / xMax) * innerW);

    for (let x = 0; x <= xMax + 1e-9; x += xStep) {
      const xx = xScale(x);
      createSVGLine(gridG, xx, 0, xx, innerH, "grid-line", root);
      const tick = createSVGGroup(axisXG, null, "tick", root);
      createSVGText(tick, fmtNum(x), xx, innerH + 8, { "text-anchor": "middle" }, root);
    }
    createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);

    const bandStep = innerH / Math.max(1, slice.length);
    const barH = Math.min(
      Math.max(3, bandStep * 0.7),
      Math.max(3, bandStep - 4),
      24
    );

    const bars = [];
    slice.forEach((student, i) => {
      const globalIdx = pageIdx * PAGE_SIZE + i;
      const cy = (i + 0.5) * bandStep;
      const avgMark = student.avgMark || 0;
      const w = xScale(avgMark);
      const displayLogin =
        student.username || student.userName || student.userFullName || `Talaba ${globalIdx + 1}`;

      bars.push({
        x: 0, y: cy - barH / 2, w, bh: barH, v: avgMark,
        userName: displayLogin, userId: student.userId,
        color: colors[globalIdx % colors.length],
        hoverColor: hovers[globalIdx % hovers.length],
        cy,
      });

      const tick = createSVGGroup(axisYG, null, "tick", root);
      createSVGText(tick, displayLogin.substring(0, 35), -8, cy, {
        "text-anchor": "end", "font-size": "12px",
        "style": "dominant-baseline:middle",
      }, root);
    });

    createSVGLine(axisYG, 0, 0, 0, innerH, "axis-line", root);

    const tStart = performance.now(), duration = 700;
    function frame(now) {
      const k = Math.min(1, (now - tStart) / duration);
      const t = easeOutC(k);
      barsG.innerHTML = "";

      bars.forEach((b) => {
        const groupEl = createSVGGroup(barsG, null, "bar-group", root);
        const wNow = Math.max(0.5, b.w * t);

        const rect = createSVGElement("rect");
        rect.setAttribute("x", b.x); rect.setAttribute("y", b.y);
        rect.setAttribute("width", wNow); rect.setAttribute("height", b.bh);
        rect.setAttribute("fill", b.color); rect.setAttribute("class", "bar");
        rect.setAttribute("rx", "3");

        const lbl = createSVGElement("text");
        const lblInside = wNow > innerW - 50;
        lbl.setAttribute("x", lblInside ? wNow - 5 : Math.max(wNow + 5, 5));
        lbl.setAttribute("y", b.cy);
        lbl.setAttribute("class", "bar-label");
        lbl.setAttribute("text-anchor", lblInside ? "end" : "start");
        lbl.setAttribute("dominant-baseline", "middle");
        lbl.setAttribute("fill", lblInside ? "#fff" : "#2b3242");
        lbl.setAttribute("font-size", "12");
        lbl.textContent = fmtNum(b.v);

        const hit = createSVGElement("rect");
        hit.setAttribute("x", b.x - 4); hit.setAttribute("y", b.y - 4);
        hit.setAttribute("width", Math.max(wNow + 8, 40));
        hit.setAttribute("height", b.bh + 8);
        hit.setAttribute("fill", "rgba(0,0,0,0)");

        groupEl.appendChild(rect); groupEl.appendChild(lbl); groupEl.appendChild(hit);

        const onEnter = (evt) => {
          rect.setAttribute("fill", b.hoverColor);
          lbl.classList.add("hover");
          tooltip.innerHTML = `<strong>${b.userName}</strong><br>O'rtacha baho: ${fmtNum(b.v)}`;
          tooltip.classList.add("show");
          tooltip.setAttribute("aria-hidden", "false");
          positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "top", 12);
        };
        const onMove = (evt) =>
          positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "top", 12);
        const onLeave = () => {
          rect.setAttribute("fill", b.color);
          lbl.classList.remove("hover");
          tooltip.classList.remove("show");
          tooltip.setAttribute("aria-hidden", "true");
        };

        hit.addEventListener("pointerenter", onEnter);
        hit.addEventListener("pointermove", onMove);
        hit.addEventListener("pointerleave", onLeave);
        hit.addEventListener("touchstart", onEnter, { passive: true });
        hit.addEventListener("touchmove", onMove, { passive: true });
        hit.addEventListener("touchend", onLeave, { passive: true });
      });

      if (k < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  // ── легенда (строится один раз по всем студентам) ──
  legend.innerHTML = "";

  const allMarks = sortedStudents.map((s) => s.avgMark).filter((m) => m > 0);
  const overallAvg =
    allMarks.length > 0 ? allMarks.reduce((sum, m) => sum + m, 0) / allMarks.length : 0;
  const maxStudentMark = allMarks.length > 0 ? Math.max(...allMarks) : 0;
  const minStudentMark = allMarks.length > 0 ? Math.min(...allMarks) : 0;

  [
    { label: "Talabalar soni", value: sortedStudents.length, color: "var(--accent)" },
    { label: "O'rtacha baho",  value: fmtNum(overallAvg),     color: "#3a6cf6"        },
    { label: "Eng yuqori",     value: fmtNum(maxStudentMark),  color: "#2ecc71"        },
    { label: "Eng past",       value: fmtNum(minStudentMark),  color: "#e74c3c"        },
  ].forEach((item) => {
    const li = document.createElement("div"); li.className = "legend-item";
    const sw = document.createElement("div"); sw.className = "swatch"; sw.style.background = item.color;
    const t  = document.createElement("div"); t.className  = "legend-title"; t.textContent = item.label;
    const v  = document.createElement("div"); v.className  = "legend-val";   v.textContent = item.value;
    li.appendChild(sw); li.appendChild(t); li.appendChild(v);
    legend.appendChild(li);
  });

  const divider = document.createElement("div");
  divider.style.cssText = "height:1px;background:#e6eaf2;margin:12px 0;";
  legend.appendChild(divider);

  const leaderTitle = document.createElement("div");
  leaderTitle.style.cssText = "font-weight:600;color:#2b3242;margin-bottom:8px;font-size:15px;";
  leaderTitle.textContent = "🏆 Top 3 liderlar";
  legend.appendChild(leaderTitle);

  sortedStudents.slice(0, 3).forEach((student, idx) => {
    const leaderItem = document.createElement("div");
    leaderItem.style.cssText = [
      "display:flex;align-items:center;gap:8px;margin-bottom:6px;padding:8px 12px;",
      "background:linear-gradient(135deg,rgba(58,108,246,.1) 0%,rgba(58,108,246,.05) 100%);",
      "border-radius:12px;font-size:12px;border:1.5px solid rgba(58,108,246,.2);",
      "cursor:pointer;position:relative;overflow:hidden;transition:all .3s cubic-bezier(.23,1,.32,1);",
      `animation:slideInLeader .6s cubic-bezier(.34,1.56,.64,1) ${idx * 0.1}s backwards;`,
    ].join("");

    const glowBg = document.createElement("div");
    glowBg.style.cssText =
      "position:absolute;inset:0;background:radial-gradient(circle at 30% 30%,rgba(58,108,246,.2) 0%,transparent 70%);opacity:0;transition:opacity .3s ease;pointer-events:none;";
    leaderItem.appendChild(glowBg);

    const medal = document.createElement("span");
    medal.style.cssText = `font-size:22px;min-width:28px;display:flex;align-items:center;justify-content:center;animation:medalBounce .6s cubic-bezier(.34,1.56,.64,1) ${idx * 0.1 + 0.2}s backwards;`;
    medal.textContent = ["🥇", "🥈", "🥉"][idx];

    const nameSpan = document.createElement("span");
    nameSpan.style.cssText = "flex:1;font-weight:600;color:#2b3242;font-size:13px;";
    nameSpan.textContent = (
      student.username || student.userName || student.userFullName || `Talaba ${idx + 1}`
    ).substring(0, 25);

    const scoreSpan = document.createElement("span");
    scoreSpan.style.cssText = `font-weight:700;color:var(--accent);font-size:14px;min-width:40px;text-align:right;animation:scoreScale .6s cubic-bezier(.34,1.56,.64,1) ${idx * 0.1 + 0.3}s backwards;`;
    scoreSpan.textContent = fmtNum(student.avgMark);

    leaderItem.appendChild(medal); leaderItem.appendChild(nameSpan); leaderItem.appendChild(scoreSpan);

    leaderItem.addEventListener("mouseenter", () => {
      leaderItem.style.transform = "translateY(-4px) scale(1.02)";
      leaderItem.style.boxShadow = "0 12px 28px rgba(58,108,246,.2)";
      glowBg.style.opacity = "1";
    });
    leaderItem.addEventListener("mouseleave", () => {
      leaderItem.style.transform = "";
      leaderItem.style.boxShadow = "";
      glowBg.style.opacity = "0";
    });

    legend.appendChild(leaderItem);
  });

  // ── первый рендер ──
  renderPage(0);
}

// Диаграмма средней оценки по предметам
export function makeSubjectsAverageChart(
  cardEl,
  subjectsData,
  chartRegistry,
  root
) {
  chartRegistry.set(cardEl.id, {
    type: "subjectsAvg",
    data: {
      subjects: JSON.parse(JSON.stringify(subjectsData)),
    },
  });

  const chart = cardEl.querySelector(".chart");
  const svg = chart.querySelector("svg");
  const tooltip = chart.querySelector(".tooltip");
  let legend = cardEl.querySelector(".legend");
  if (!legend) {
    legend = document.createElement("div");
    legend.className = "legend";
    cardEl.appendChild(legend);
  }

  const WIDTH = 960,
    HEIGHT = 460;
  const M = { top: 18, right: 60, bottom: 44, left: 280 };
  const innerW = WIDTH - M.left - M.right;
  const innerH = HEIGHT - M.top - M.bottom;

  svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const g = createSVGGroup(svg, `translate(${M.left},${M.top})`, null, root);
  const gridG = createSVGGroup(g, null, null, root);
  const barsG = createSVGGroup(g, null, null, root);
  const axisXG = createSVGGroup(g, null, null, root);
  const axisYG = createSVGGroup(g, null, null, root);

  if (!subjectsData || subjectsData.length === 0) {
    createSVGLine(axisXG, 0, 0, innerW, 0, null, root);
    legend.innerHTML = "";
    const emptyItem = document.createElement("div");
    emptyItem.className = "legend-item";
    emptyItem.textContent = "Ma'lumot yo'q";
    legend.appendChild(emptyItem);
    return;
  }

  const sortedSubjects = [...subjectsData].sort(
    (a, b) => (b.avgMark || 0) - (a.avgMark || 0)
  );

  const bars = [];
  let maxMark = 100;
  const xStep = niceStep(maxMark, 6);
  const xMax = Math.ceil(maxMark / xStep) * xStep || 1;

  const xScale = (val) => Math.round((val / xMax) * innerW);

  for (let x = 0; x <= xMax + 1e-9; x += xStep) {
    const xx = xScale(x);
    createSVGLine(gridG, xx, 0, xx, innerH, "grid-line", root);
    const tick = createSVGGroup(axisXG, null, "tick", root);
    createSVGText(tick, fmtNum(x), xx, innerH + 8, { "text-anchor": "middle" }, root);
  }
  createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);

  const { colors, hovers } = buildPalette(sortedSubjects.length);

  const bandStep = innerH / Math.max(1, sortedSubjects.length);
  const barH = Math.min(
    Math.max(3, bandStep * 0.7),
    Math.max(3, bandStep - 4),
    28
  );

  sortedSubjects.forEach((subject, i) => {
    const cy = (i + 0.5) * bandStep;
    const y = cy - barH / 2;
    const avgMark = subject.avgMark || 0;
    const w = xScale(avgMark);

    const displayName = subject.subjectName || `Fan ${i + 1}`;

    bars.push({
      x: 0, y, w, bh: barH, v: avgMark,
      subjectName: displayName,
      subjectId: subject.subjectId,
      testCount: subject.testCount || 0,
      color: colors[i],
      hoverColor: hovers[i],
      cy,
    });

    const tick = createSVGGroup(axisYG, null, "tick", root);
    createSVGText(tick, displayName.substring(0, 35), -8, cy, {
      "text-anchor": "end",
      "font-size": "12px",
      "style": "dominant-baseline:middle",
    }, root);
  });

  createSVGLine(axisYG, 0, 0, 0, innerH, "axis-line", root);

  const tStart = performance.now(),
    duration = 900;
  function frame(now) {
    const k = Math.min(1, (now - tStart) / duration);
    const t = easeOutC(k);
    barsG.innerHTML = "";

    bars.forEach((b) => {
      const groupEl = createSVGGroup(barsG, null, "bar-group", root);

      const wNow = Math.max(0.5, b.w * t);

      const rect = createSVGElement("rect");
      rect.setAttribute("x", b.x);
      rect.setAttribute("y", b.y);
      rect.setAttribute("width", wNow);
      rect.setAttribute("height", b.bh);
      rect.setAttribute("fill", b.color);
      rect.setAttribute("class", "bar");
      rect.setAttribute("rx", "3");

      const lbl = createSVGElement("text");
      const lblInside = wNow > innerW - 50;
      lbl.setAttribute("x", lblInside ? wNow - 5 : Math.max(wNow + 5, 5));
      lbl.setAttribute("y", b.cy);
      lbl.setAttribute("class", "bar-label");
      lbl.setAttribute("text-anchor", lblInside ? "end" : "start");
      lbl.setAttribute("dominant-baseline", "middle");
      lbl.setAttribute("fill", lblInside ? "#fff" : "#2b3242");
      lbl.setAttribute("font-size", "12");
      lbl.textContent = fmtNum(b.v);

      const hit = createSVGElement("rect");
      hit.setAttribute("x", b.x - 4);
      hit.setAttribute("y", b.y - 4);
      hit.setAttribute("width", Math.max(wNow + 8, 40));
      hit.setAttribute("height", b.bh + 8);
      hit.setAttribute("fill", "rgba(0,0,0,0)");

      groupEl.appendChild(rect);
      groupEl.appendChild(lbl);
      groupEl.appendChild(hit);

      const onEnter = (evt) => {
        rect.setAttribute("fill", b.hoverColor);
        lbl.classList.add("hover");
        tooltip.innerHTML = `<strong>${b.subjectName}</strong><br>O'rtacha baho: ${fmtNum(
          b.v
        )}<br>Nazoratlar: ${b.testCount}`;
        tooltip.classList.add("show");
        tooltip.setAttribute("aria-hidden", "false");
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "top", 12);
      };
      const onMove = (evt) =>
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "top", 12);
      const onLeave = () => {
        rect.setAttribute("fill", b.color);
        lbl.classList.remove("hover");
        tooltip.classList.remove("show");
        tooltip.setAttribute("aria-hidden", "true");
      };

      hit.addEventListener("pointerenter", onEnter);
      hit.addEventListener("pointermove", onMove);
      hit.addEventListener("pointerleave", onLeave);
      hit.addEventListener("touchstart", onEnter, { passive: true });
      hit.addEventListener("touchmove", onMove, { passive: true });
      hit.addEventListener("touchend", onLeave, { passive: true });
    });

    if (k < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  legend.innerHTML = "";

  const allMarks = sortedSubjects
    .map((s) => s.avgMark)
    .filter((m) => m > 0);
  const overallAvg =
    allMarks.length > 0 ? allMarks.reduce((sum, m) => sum + m, 0) / allMarks.length : 0;
  const maxSubjectMark = allMarks.length > 0 ? Math.max(...allMarks) : 0;
  const minSubjectMark = allMarks.length > 0 ? Math.min(...allMarks) : 0;

  const legendItems = [
    { label: "Fanlar soni", value: sortedSubjects.length, color: "var(--accent)" },
    { label: "O'rtacha baho", value: fmtNum(overallAvg), color: "#3a6cf6" },
    { label: "Eng yuqori", value: fmtNum(maxSubjectMark), color: "#2ecc71" },
    { label: "Eng past", value: fmtNum(minSubjectMark), color: "#e74c3c" },
  ];

  legendItems.forEach((item) => {
    const legendItem = document.createElement("div");
    legendItem.className = "legend-item";
    const sw = document.createElement("div");
    sw.className = "swatch";
    sw.style.background = item.color;
    const title = document.createElement("div");
    title.className = "legend-title";
    title.textContent = item.label;
    const val = document.createElement("div");
    val.className = "legend-val";
    val.textContent = item.value;
    legendItem.appendChild(sw);
    legendItem.appendChild(title);
    legendItem.appendChild(val);
    legend.appendChild(legendItem);
  });

  const divider = document.createElement("div");
  divider.style.height = "1px";
  divider.style.background = "#e6eaf2";
  divider.style.margin = "12px 0";
  legend.appendChild(divider);

  const topThree = sortedSubjects.slice(0, 3);
  const leaderTitle = document.createElement("div");
  leaderTitle.style.fontWeight = "600";
  leaderTitle.style.color = "#2b3242";
  leaderTitle.style.marginBottom = "8px";
  leaderTitle.style.fontSize = "32px";
  leaderTitle.textContent = "🏆 Eng yaxshi fanlar";
  legend.appendChild(leaderTitle);

  topThree.forEach((subject, idx) => {
    const leaderItem = document.createElement("div");
    leaderItem.style.display = "flex";
    leaderItem.style.alignItems = "center";
    leaderItem.style.gap = "8px";
    leaderItem.style.marginBottom = "6px";
    leaderItem.style.padding = "8px 12px";
    leaderItem.style.background = "linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(46, 204, 113, 0.05) 100%)";
    leaderItem.style.borderRadius = "12px";
    leaderItem.style.fontSize = "13px";
    leaderItem.style.border = "1.5px solid rgba(46, 204, 113, 0.2)";
    leaderItem.style.backdropFilter = "blur(8px)";
    leaderItem.style.transition = "all 0.3s cubic-bezier(0.23, 1, 0.32, 1)";
    leaderItem.style.cursor = "pointer";
    leaderItem.style.position = "relative";
    leaderItem.style.overflow = "hidden";
    leaderItem.style.animation = `slideInLeader 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.1}s backwards`;

    // Glow effect background - green shade for subjects
    const glowBg = document.createElement("div");
    glowBg.style.position = "absolute";
    glowBg.style.inset = "0";
    glowBg.style.background = "radial-gradient(circle at 30% 30%, rgba(46, 204, 113, 0.2) 0%, transparent 70%)";
    glowBg.style.opacity = "0";
    glowBg.style.transition = "opacity 0.3s ease";
    glowBg.style.pointerEvents = "none";
    leaderItem.appendChild(glowBg);

    const medal = document.createElement("span");
    medal.style.fontSize = "20px";
    medal.style.fontWeight = "700";
    medal.style.minWidth = "28px";
    medal.style.display = "flex";
    medal.style.alignItems = "center";
    medal.style.justifyContent = "center";
    medal.style.animation = `medalBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.1 + 0.2}s backwards`;
    const medals = ["🥇", "🥈", "🥉"];
    medal.textContent = medals[idx];

    const nameSpan = document.createElement("span");
    nameSpan.style.flex = "1";
    nameSpan.style.fontWeight = "600";
    nameSpan.style.color = "#2b3242";
    nameSpan.style.fontSize = "13px";
    nameSpan.style.letterSpacing = "0.3px";
    const subjectName = subject && subject.subjectName ? subject.subjectName : `Fan ${idx + 1}`;
    nameSpan.textContent = subjectName.substring(0, 25);

    const scoreSpan = document.createElement("span");
    scoreSpan.style.fontWeight = "700";
    scoreSpan.style.color = "#2ecc71";
    scoreSpan.style.fontSize = "13px";
    scoreSpan.style.minWidth = "40px";
    scoreSpan.style.textAlign = "right";
    scoreSpan.style.animation = `scoreScale 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.1 + 0.3}s backwards`;
    scoreSpan.textContent = subject && subject.avgMark ? fmtNum(subject.avgMark) : "0.00";

    leaderItem.appendChild(medal);
    leaderItem.appendChild(nameSpan);
    leaderItem.appendChild(scoreSpan);

    // Hover effects
    leaderItem.addEventListener("mouseenter", () => {
      leaderItem.style.transform = "translateY(-4px) scale(1.02)";
      leaderItem.style.boxShadow = "0 12px 28px rgba(46, 204, 113, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.3)";
      glowBg.style.opacity = "1";
      medal.style.transform = "scale(1.15) rotate(10deg)";
      scoreSpan.style.transform = "scale(1.1)";
    });

    leaderItem.addEventListener("mouseleave", () => {
      leaderItem.style.transform = "translateY(0) scale(1)";
      leaderItem.style.boxShadow = "none";
      glowBg.style.opacity = "0";
      medal.style.transform = "scale(1) rotate(0deg)";
      scoreSpan.style.transform = "scale(1)";
    });

    legend.appendChild(leaderItem);
  });
}
